import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { ToolShell } from "@/components/tools/ToolShell";
import { getTool } from "@/lib/tools-catalog";
import { getToolContent } from "@/lib/tool-content";
import { ToolBody } from "./free-tools/$slug";

export const Route = createFileRoute("/$slug")({
  loader: ({ params }) => {
    if (params.slug === "fr") throw redirect({ to: "/fr", statusCode: 301 });
    if (params.slug === "pt-br") throw redirect({ to: "/pt-br", statusCode: 301 });
    if (params.slug === "pdf-tools") throw redirect({ to: "/pdf-tools", statusCode: 301 });
    if (params.slug === "about") throw redirect({ to: "/about", statusCode: 301 });
    if (params.slug === "contact") throw redirect({ to: "/contact", statusCode: 301 });
    if (params.slug === "privacy-policy") throw redirect({ to: "/privacy-policy", statusCode: 301 });
    if (params.slug === "terms-of-use") throw redirect({ to: "/terms-of-use", statusCode: 301 });
    if (params.slug === "disclaimer") throw redirect({ to: "/disclaimer", statusCode: 301 });

    const tool = getTool(params.slug);
    if (!tool) throw notFound();
    return { tool };
  },
  head: ({ params }) => {
    const tool = getTool(params.slug);
    const content = getToolContent(params.slug, tool ? tool.name : params.slug);
    const canonicalUrl = `https://picamini.online/${params.slug}`;
    const toolName = tool ? tool.name : params.slug;

    const webAppSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": `${toolName} - Picamini.online`,
      "url": canonicalUrl,
      "description": content.seoDescription,
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "All",
      "inLanguage": "en",
      "browserRequirements": "Requires JavaScript. Requires HTML5.",
      "datePublished": "2024-01-15",
      "dateModified": "2026-09-15",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
      },
      "featureList": content.features || [],
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://picamini.online/",
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": toolName,
          "item": canonicalUrl,
        },
      ],
    };

    const faqSchema = content.faq && content.faq.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": content.faq.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      })),
    } : null;

    const scripts: Array<{ type: string; children: string }> = [
      {
        type: "application/ld+json",
        children: JSON.stringify(webAppSchema),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbSchema),
      },
    ];

    // HowTo schema — helps rank for "how to use" queries
    const howToSchema = content.howTo && content.howTo.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": `How to Use the ${toolName}`,
      "description": content.seoDescription,
      "tool": [{ "@type": "HowToTool", "name": "Web Browser" }],
      "step": content.howTo.map((step: string, index: number) => ({
        "@type": "HowToStep",
        "position": index + 1,
        "name": `Step ${index + 1}`,
        "text": step,
      })),
    } : null;

    if (faqSchema) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(faqSchema),
      });
    }

    if (howToSchema) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(howToSchema),
      });
    }

    return {
      meta: [
        { title: content.seoTitle },
        { name: "description", content: content.seoDescription },
        {
          name: "keywords",
          content: tool?.keywords?.join(", ") || `${toolName.toLowerCase()}, free online ${toolName.toLowerCase()}, classroom tools, picamini`,
        },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: content.seoTitle },
        { property: "og:description", content: content.seoDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:image", content: "https://picamini.online/og-image.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `${toolName} - Free Classroom Tool on Picamini.online` },
        { property: "og:locale", content: "en_US" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: content.seoTitle },
        { name: "twitter:description", content: content.seoDescription },
        { name: "twitter:image", content: "https://picamini.online/og-image.png" },
        { name: "theme-color", content: "#4F46E5" },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl },
        { rel: "alternate", hrefLang: "en", href: canonicalUrl },
        { rel: "alternate", hrefLang: "x-default", href: canonicalUrl },
      ],
      scripts,
    };
  },
  component: ToolPage,
});

function ToolPage() {
  const { tool } = Route.useLoaderData();
  const content = getToolContent(tool.slug, tool.name);

  return (
    <ToolShell tool={tool} content={content}>
      <ToolBody key={tool.slug} slug={tool.slug} />
    </ToolShell>
  );
}